import Toggle from '../components/Toggle';
import ValuablesDetails from '../components/ValuablesDetails';
import { ic, lc } from '../helpers';

// ─────────────────────────────────────────
// STEP 6 — LIABILITY & VALUABLES
// Umbrella + rental homes + six valuables categories.
// ─────────────────────────────────────────
export default function Step6Liability({ liability, setLiability, error }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5">
      <h2 className="text-base font-semibold text-slate-800 pb-1 border-b">Liability & Scheduled Property</h2>

      {/* Liability exposures */}
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-2">Liability Exposures</p>
        <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">
          <Toggle
            label="Do you have an umbrella policy?"
            checked={liability.hasUmbrella}
            onChange={v => setLiability(p => ({ ...p, hasUmbrella: v }))}
          />
          <Toggle
            label="Do you own any rental or secondary homes?"
            checked={liability.hasRentalHomes}
            onChange={v => setLiability(p => ({ ...p, hasRentalHomes: v }))}
          />
          {liability.hasRentalHomes && (
            <div className="py-3">
              <label className={lc}>How many rental/secondary homes?</label>
              <input
                type="number"
                min="1"
                value={liability.rentalHomesCount}
                onChange={e => setLiability(p => ({ ...p, rentalHomesCount: e.target.value }))}
                placeholder="e.g. 2"
                className={ic}
              />
            </div>
          )}
        </div>
      </div>

      {/* Valuables coverage */}
      <div>
        <p className="text-sm font-semibold text-slate-700 mb-2">Valuables Coverage</p>
        <p className="text-xs text-slate-500 mb-3">
          Tell us which valuables you own so we can quote proper coverage. For each category, you can split coverage between{' '}
          <span className="font-medium">Scheduled</span> (individually appraised items) and{' '}
          <span className="font-medium">Blanket</span> (a single lump-sum limit).
        </p>

        <div className="bg-slate-50 rounded-xl px-4 divide-y divide-slate-200">
          {/* Jewelry */}
          <Toggle
            label="Do you own jewelry you'd like to insure?"
            checked={liability.ownsJewelry}
            onChange={v => setLiability(p => ({ ...p, ownsJewelry: v }))}
          />
          {liability.ownsJewelry && (
            <div className="py-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lc}>Scheduled jewelry total ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={liability.jewelryScheduledAmount}
                    onChange={e => setLiability(p => ({ ...p, jewelryScheduledAmount: e.target.value }))}
                    placeholder="e.g. 25000"
                    className={ic}
                  />
                </div>
                <div>
                  <label className={lc}>Blanket jewelry total ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={liability.jewelryBlanketAmount}
                    onChange={e => setLiability(p => ({ ...p, jewelryBlanketAmount: e.target.value }))}
                    placeholder="e.g. 10000"
                    className={ic}
                  />
                </div>
              </div>
              <div>
                <label className={lc}>Of that, jewelry kept in a bank vault ($)</label>
                <input
                  type="number"
                  min="0"
                  value={liability.jewelryInBankVault}
                  onChange={e => setLiability(p => ({ ...p, jewelryInBankVault: e.target.value }))}
                  placeholder="optional"
                  className={ic}
                />
              </div>
              <ValuablesDetails category="jewelry" liability={liability} setLiability={setLiability} />
            </div>
          )}

          {/* Fine Art */}
          <Toggle
            label="Do you own fine art or collectibles?"
            checked={liability.ownsFineArt}
            onChange={v => setLiability(p => ({ ...p, ownsFineArt: v }))}
          />
          {liability.ownsFineArt && (
            <div className="py-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lc}>Scheduled fine art / collectibles ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={liability.fineArtScheduledAmount}
                    onChange={e => setLiability(p => ({ ...p, fineArtScheduledAmount: e.target.value }))}
                    placeholder="e.g. 75000"
                    className={ic}
                  />
                </div>
                <div>
                  <label className={lc}>Blanket fine art / collectibles ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={liability.fineArtBlanketAmount}
                    onChange={e => setLiability(p => ({ ...p, fineArtBlanketAmount: e.target.value }))}
                    placeholder="optional"
                    className={ic}
                  />
                </div>
              </div>
              <ValuablesDetails category="fineArt" liability={liability} setLiability={setLiability} />
            </div>
          )}

          {/* Coins / Silver / Stamps / Furs */}
          <Toggle
            label="Do you own coins, silver, stamps, or furs?"
            checked={liability.ownsCoins}
            onChange={v => setLiability(p => ({ ...p, ownsCoins: v }))}
          />
          {liability.ownsCoins && (
            <div className="py-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lc}>Scheduled coins / silver / stamps / furs ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={liability.coinsScheduledAmount}
                    onChange={e => setLiability(p => ({ ...p, coinsScheduledAmount: e.target.value }))}
                    placeholder="optional"
                    className={ic}
                  />
                </div>
                <div>
                  <label className={lc}>Blanket coins / silver / stamps / furs ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={liability.coinsBlanketAmount}
                    onChange={e => setLiability(p => ({ ...p, coinsBlanketAmount: e.target.value }))}
                    placeholder="optional"
                    className={ic}
                  />
                </div>
              </div>
              <ValuablesDetails category="coins" liability={liability} setLiability={setLiability} />
            </div>
          )}

          {/* Wine */}
          <Toggle
            label="Do you own a wine collection?"
            checked={liability.ownsWine}
            onChange={v => setLiability(p => ({ ...p, ownsWine: v }))}
          />
          {liability.ownsWine && (
            <div className="py-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lc}>Scheduled wine ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={liability.wineScheduledAmount}
                    onChange={e => setLiability(p => ({ ...p, wineScheduledAmount: e.target.value }))}
                    placeholder="optional"
                    className={ic}
                  />
                </div>
                <div>
                  <label className={lc}>Blanket wine ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={liability.wineBlanketAmount}
                    onChange={e => setLiability(p => ({ ...p, wineBlanketAmount: e.target.value }))}
                    placeholder="optional"
                    className={ic}
                  />
                </div>
              </div>
              <ValuablesDetails category="wine" liability={liability} setLiability={setLiability} />
            </div>
          )}

          {/* Musical Instruments */}
          <Toggle
            label="Do you own musical instruments?"
            checked={liability.ownsMusical}
            onChange={v => setLiability(p => ({ ...p, ownsMusical: v }))}
          />
          {liability.ownsMusical && (
            <div className="py-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={lc}>Scheduled musical instruments ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={liability.musicalScheduledAmount}
                    onChange={e => setLiability(p => ({ ...p, musicalScheduledAmount: e.target.value }))}
                    placeholder="optional"
                    className={ic}
                  />
                </div>
                <div>
                  <label className={lc}>Blanket musical instruments ($)</label>
                  <input
                    type="number"
                    min="0"
                    value={liability.musicalBlanketAmount}
                    onChange={e => setLiability(p => ({ ...p, musicalBlanketAmount: e.target.value }))}
                    placeholder="optional"
                    className={ic}
                  />
                </div>
              </div>
              <ValuablesDetails category="musical" liability={liability} setLiability={setLiability} />
            </div>
          )}

          {/* Miscellaneous */}
          <Toggle
            label="Other miscellaneous valuable items? (cameras, sports gear, etc.)"
            checked={liability.ownsMisc}
            onChange={v => setLiability(p => ({ ...p, ownsMisc: v }))}
          />
          {liability.ownsMisc && (
            <div className="py-3">
              <label className={lc}>Scheduled miscellaneous items ($)</label>
              <input
                type="number"
                min="0"
                value={liability.miscScheduledAmount}
                onChange={e => setLiability(p => ({ ...p, miscScheduledAmount: e.target.value }))}
                placeholder="optional"
                className={ic}
              />
              <p className="text-xs text-slate-500 mt-1">
                Note: miscellaneous items must be individually scheduled — no blanket option.
              </p>
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
    </div>
  );
}